// Theme: built-in and saved themes, and the colour tokens components use.
import { useCallback, useMemo, useRef, useState } from 'react'
import {
  Check, ChevronDown, Copy, Download, ImagePlus, Loader2, MoreHorizontal, Paintbrush2, Pencil, Plus, RefreshCw, Save, Trash2, Upload, Users,
} from 'lucide-react'
import { THEME_TOKEN_DEFAULTS } from '../../context/BrandingContext'
import {
  Collapse, ColorField, ConfirmDialog, LowContrast, Panel, PromptDialog, ResetButton, useDismiss,
} from './SettingsPrimitives'
import { TOKEN_GROUPS, TOKEN_KEYS, sameColor } from './settingsModel'
import { THEME_PRESETS } from './themePresets'
import {
  MAX_THEME_NAME, createTheme, exportThemes, nameError, parseThemeFile, themeMatches, uniqueName, useSharedThemeLibrary,
} from './themeLibrary'
import { logoThemes, useLogoColors } from './logoPalette'

const LIVE_GROUPS = TOKEN_GROUPS.filter(group => group.live)
// Every group of tiles shares this grid, so cards line up across groups.
const TILE_GRID = 'grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(9.25rem,1fr))]'
const OTHER_GROUP = TOKEN_GROUPS.find(group => !group.live)

// Text/background pairs worth checking, keyed by the row that shows the warning.
const CONTRAST_PAIRS = {
  primary: t => [t.primary_text, t.primary],
  success: t => ['#ffffff', t.success],
  error: t => ['#ffffff', t.error],
}

const slug = name => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'theme'

function downloadFile(filename, text) {
  const href = URL.createObjectURL(new Blob([text], { type: 'application/json' }))
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.click()
  URL.revokeObjectURL(href)
}

function TileMenu({ items, label }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(open, close, ref)

  return (
    <div ref={ref} className="absolute bottom-2 right-1.5">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${label} options`}
        className="grid h-6 w-6 place-items-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 dark:hover:bg-gray-800 dark:hover:text-gray-200"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 w-52 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg animate-panel-in
                     dark:border-gray-700 dark:bg-gray-900"
        >
          {items.filter(Boolean).map(({ label: text, icon: Icon, onClick, danger }) => (
            <button
              key={text}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onClick() }}
              className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-[13px] transition-colors
                          ${danger
                            ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                            : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800'}`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {text}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// A tiny picture of the app in this theme: sidebar, a button, status dots and a toggle.
function ThemeMock({ theme }) {
  const t = theme.tokens
  return (
    <span aria-hidden="true" className="flex h-[4.5rem] overflow-hidden rounded-lg ring-1 ring-inset ring-black/5 dark:ring-white/5">
      <span className="flex w-[30%] flex-col gap-1 p-1.5 transition-colors duration-300" style={{ backgroundColor: theme.primary_color }}>
        <span className="mb-0.5 h-1.5 w-3/4 rounded-sm bg-white/70" />
        <span className="h-1 w-full rounded-sm bg-white/35" />
        <span className="h-1 w-2/3 rounded-sm bg-white/25" />
        <span className="h-1 w-5/6 rounded-sm bg-white/25" />
      </span>
      <span className="flex flex-1 flex-col justify-between bg-gray-50 p-1.5 dark:bg-gray-800">
        <span className="flex items-center justify-between gap-1">
          <span className="h-1.5 w-8 rounded-sm bg-gray-300 dark:bg-gray-600" />
          <span className="grid h-3.5 w-10 place-items-center rounded transition-colors duration-300" style={{ backgroundColor: t.primary }}>
            <span className="h-[3px] w-5 rounded-full opacity-90" style={{ backgroundColor: t.primary_text }} />
          </span>
        </span>
        <span className="flex items-center gap-1 rounded bg-white px-1 py-1 shadow-[0_0_0_1px_rgba(0,0,0,0.04)] dark:bg-gray-900">
          {[t.success, t.warning, t.error, t.info].map((color, i) => (
            <span key={i} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
          ))}
          <span className="ml-auto flex h-2.5 w-4 items-center justify-end rounded-full px-[2px]" style={{ backgroundColor: t.toggle_on }}>
            <span className="h-1.5 w-1.5 rounded-full bg-white" />
          </span>
        </span>
      </span>
    </span>
  )
}

function ThemeTile({ tile, selected, live, edited, onPick, onPreview, disabled, menu }) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => onPick(tile)}
        onMouseEnter={() => onPreview(tile)}
        onMouseLeave={() => onPreview(null)}
        onFocus={() => onPreview(tile)}
        onBlur={() => onPreview(null)}
        disabled={disabled}
        aria-pressed={selected}
        aria-label={`${tile.name}${live ? ' (in use)' : ''}`}
        title={tile.caption ? `${tile.name}: ${tile.caption}` : tile.name}
        className={`block w-full rounded-xl border p-2 text-left transition-[border-color,box-shadow,transform] duration-200
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 enabled:hover:-translate-y-0.5
                    disabled:cursor-not-allowed disabled:opacity-60
                    ${selected
                      ? 'border-navy-500 ring-4 ring-navy-500/15'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm dark:border-gray-700 dark:hover:border-gray-600'}`}
      >
        <ThemeMock theme={tile} />
        <span className={`mt-2 flex h-5 items-center gap-1.5 px-0.5 ${menu ? 'pr-6' : ''}`}>
          <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-gray-800 dark:text-gray-200">{tile.name}</span>
          {live && (
            <span className="shrink-0 rounded bg-emerald-50 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-emerald-700
                             ring-1 ring-inset ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:ring-emerald-800/60">
              In use
            </span>
          )}
          {edited && <span title="Colours changed since this theme was saved" className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />}
          <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-full bg-navy-600 text-white transition-[opacity,transform] duration-200
                            ${selected ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`}>
            <Check className="h-2.5 w-2.5" strokeWidth={3} />
          </span>
        </span>
      </button>
      {menu && <TileMenu items={menu} label={tile.name} />}
    </div>
  )
}

function TokenRow({ token, value, saved, onChange, disabled, error, pair, bindSpot }) {
  const def = THEME_TOKEN_DEFAULTS[token.key]
  const custom = !sameColor(value, def)
  const unsaved = !sameColor(value, saved)
  const note = error || token.hint

  return (
    <div {...bindSpot(token.key)} className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-gray-800 dark:text-gray-200">{token.label}</span>
          <span
            title="Unsaved"
            className={`h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500 transition-[opacity,transform] duration-200
                        ${unsaved ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}
          />
          {pair && <LowContrast fg={pair[0]} bg={pair[1]} />}
        </div>
        {note && (
          <p className={`text-xs ${error ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>{note}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <ColorField
          id={`settings-token-${token.key}`}
          size="sm"
          align="right"
          value={value}
          onChange={onChange}
          disabled={disabled}
          invalid={Boolean(error)}
          fallback={def}
          ariaLabel={token.label}
        />
        <ResetButton visible={custom} onClick={() => onChange(def)} disabled={disabled} label={`Reset ${token.label.toLowerCase()}`} />
      </div>
    </div>
  )
}

// Where the draft colours stand against the theme being edited, with the matching actions.
function DraftBanner({ editing, modified, unsaved, onUpdate, onSaveAs, onRevert, disabled }) {
  if (disabled || (!editing && !unsaved)) return null
  const quiet = 'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12.5px] font-medium text-gray-600 transition-colors hover:bg-white hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
  const solid = 'inline-flex items-center gap-1.5 rounded-md bg-navy-600 px-2.5 py-1 text-[12.5px] font-semibold text-white transition-colors hover:bg-navy-700'

  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 animate-panel-in
                    dark:border-gray-800 dark:bg-gray-800/40">
      <p className="min-w-0 text-[13px] text-gray-600 dark:text-gray-300">
        {editing ? (
          <>
            Editing <span className="font-semibold text-gray-900 dark:text-gray-100">{editing.name}</span>
            {modified && <span className="text-gray-500 dark:text-gray-400"> · changes not in the theme yet</span>}
          </>
        ) : (
          'These colours aren’t saved as a theme.'
        )}
      </p>
      <div className="flex items-center gap-1">
        {editing && modified && (
          <>
            <button type="button" onClick={onRevert} className={quiet}>Revert</button>
            <button type="button" onClick={onSaveAs} className={quiet}>Save as new</button>
            <button type="button" onClick={onUpdate} className={solid}><Save className="h-3.5 w-3.5" /> Update theme</button>
          </>
        )}
        {!editing && (
          <button type="button" onClick={onSaveAs} className={solid}><Save className="h-3.5 w-3.5" /> Save as theme</button>
        )}
      </div>
    </div>
  )
}

export default function ThemeSection({
  tokens, savedTokens, savedPrimary, primaryColor, setToken, onPickTheme, onPreviewTheme, onResetTokens, errors, disabled, bindSpot,
  themeLibrary, saveThemeLibrary, toast, logoUrl, logoSampleUrl,
}) {
  const [showOther, setShowOther] = useState(false)
  const otherOpen = showOther || OTHER_GROUP.tokens.some(token => errors[`token:${token.key}`])
  const customised = TOKEN_KEYS.some(key => !sameColor(tokens[key], THEME_TOKEN_DEFAULTS[key]))

  const library = useSharedThemeLibrary(themeLibrary, saveThemeLibrary)
  const [editingId, setEditingId] = useState(null)
  const [dialog, setDialog] = useState(null) // { mode: 'create' | 'saveAs' | 'rename', theme? }
  const [startFrom, setStartFrom] = useState('current')
  const [deleting, setDeleting] = useState(null)
  const [busy, setBusy] = useState(false)
  const fileRef = useRef(null)
  const logoFileRef = useRef(null)

  const logo = useLogoColors(logoUrl, logoSampleUrl)
  const suggestions = useMemo(() => logoThemes(logo.colors, THEME_TOKEN_DEFAULTS), [logo.colors])

  const current = { primary_color: primaryColor, tokens }
  const allThemes = [...THEME_PRESETS, ...library.themes]
  const tiles = [...allThemes, ...suggestions]
  const editing = library.themes.find(t => t.id === editingId) || null
  const modified = Boolean(editing) && !themeMatches(editing, primaryColor, tokens)
  const matchesAny = tiles.some(t => themeMatches(t, primaryColor, tokens))

  // The theme in use, when it isn't one of the tiles already.
  const savedTheme = { id: 'saved', name: 'Current theme', primary_color: savedPrimary, tokens: savedTokens }
  const savedIsTile = tiles.some(t => themeMatches(t, savedPrimary, savedTokens))
  const selectedId = (editing && !modified ? editing.id : null)
    ?? tiles.find(t => themeMatches(t, primaryColor, tokens))?.id
    ?? (themeMatches(savedTheme, primaryColor, tokens) ? 'saved' : null)

  // Library writes go to the server, shared by every admin. Wraps a commit
  // with the busy flag so two clicks in a row can't race, and reports a
  // failed save (network, or another admin's edit invalidating this one).
  const stored = async promise => {
    setBusy(true)
    try {
      const ok = await promise
      if (!ok) toast.error("Couldn't save the theme", 'Try again in a moment.')
      return ok
    } finally {
      setBusy(false)
    }
  }

  // Dialogs keep their last contents while they animate closed.
  const lastDialog = useRef(null)
  if (dialog) lastDialog.current = dialog
  const shownDialog = dialog || lastDialog.current
  const lastDeleting = useRef(null)
  if (deleting) lastDeleting.current = deleting
  const shownDeleting = deleting || lastDeleting.current

  const pick = theme => {
    setEditingId(theme.builtIn || theme.suggested || theme.id === 'saved' ? null : theme.id)
    onPickTheme(theme)
  }

  const exportOne = theme => downloadFile(`${slug(theme.name)}.theme.json`, exportThemes([theme]))

  const duplicate = async theme => {
    const copy = createTheme({ ...theme, name: uniqueName(`${theme.name} copy`, allThemes) }, THEME_TOKEN_DEFAULTS)
    if (await stored(library.add([copy]))) toast.success('Theme duplicated', copy.name)
  }

  // Suggestions are saved as-is, then opened for editing.
  const keepSuggestion = async theme => {
    const saved = createTheme({ ...theme, name: uniqueName(theme.name, allThemes) }, THEME_TOKEN_DEFAULTS)
    if (!(await stored(library.add([saved])))) return
    setEditingId(saved.id)
    onPickTheme(saved)
    toast.success('Theme saved', saved.name)
  }

  const updateFromDraft = async theme => {
    if (!(await stored(library.update(theme.id, { primary_color: primaryColor, tokens: { ...tokens } })))) return
    toast.success('Theme updated', `${theme.name} now uses these colours. Press Save to apply them for everyone.`)
  }

  const confirmDialog = async name => {
    if (dialog.mode === 'rename') {
      await stored(library.update(dialog.theme.id, { name }))
    } else {
      const base = dialog.mode === 'create' && startFrom !== 'current'
        ? tiles.find(t => t.id === startFrom) || current
        : current
      const theme = createTheme({ ...base, name }, THEME_TOKEN_DEFAULTS)
      if (!(await stored(library.add([theme])))) { setDialog(null); return }
      setEditingId(theme.id)
      if (base !== current) onPickTheme(theme)
      toast.success(dialog.mode === 'create' ? 'Theme created' : 'Theme saved', name)
    }
    setDialog(null)
  }

  const confirmDelete = async () => {
    if (await stored(library.remove(deleting.id))) toast.success('Theme deleted', deleting.name)
    if (editingId === deleting.id) setEditingId(null)
    setDeleting(null)
  }

  const importFile = async e => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      const incoming = parseThemeFile(await file.text(), THEME_TOKEN_DEFAULTS)
      const taken = [...allThemes]
      const named = incoming.map(theme => {
        const next = { ...theme, name: uniqueName(theme.name, taken) }
        taken.push(next)
        return next
      })
      if (!(await stored(library.add(named)))) return
      toast.success(named.length === 1 ? 'Theme imported' : `${named.length} themes imported`, named.map(t => t.name).join(', '))
    } catch (err) {
      toast.error("Couldn't import themes", err.message)
    }
  }

  const openCreate = () => { setStartFrom('current'); setDialog({ mode: 'create' }) }
  const closeDialog = useCallback(() => setDialog(null), [])
  const closeDelete = useCallback(() => setDeleting(null), [])

  const suggestionMenu = theme => (disabled ? null : [
    { label: 'Save as theme', icon: Save, onClick: () => keepSuggestion(theme) },
    { label: 'Export', icon: Download, onClick: () => exportOne(theme) },
  ])
  const builtInMenu = theme => (disabled ? null : [
    { label: 'Duplicate to edit', icon: Copy, onClick: () => duplicate(theme) },
    { label: 'Export', icon: Download, onClick: () => exportOne(theme) },
  ])
  const customMenu = theme => (disabled ? null : [
    { label: 'Edit colours', icon: Paintbrush2, onClick: () => pick(theme) },
    { label: 'Rename', icon: Pencil, onClick: () => setDialog({ mode: 'rename', theme }) },
    !themeMatches(theme, primaryColor, tokens) && { label: 'Replace with current colours', icon: RefreshCw, onClick: () => updateFromDraft(theme) },
    { label: 'Duplicate', icon: Copy, onClick: () => duplicate(theme) },
    { label: 'Export', icon: Download, onClick: () => exportOne(theme) },
    { label: 'Delete', icon: Trash2, onClick: () => setDeleting(theme), danger: true },
  ])

  const tile = (theme, menu) => (
    <ThemeTile
      key={theme.id}
      tile={theme}
      selected={theme.id === selectedId}
      live={themeMatches(theme, savedPrimary, savedTokens)}
      edited={theme.id === editingId && modified}
      onPick={pick}
      onPreview={onPreviewTheme}
      disabled={disabled || busy}
      menu={menu}
    />
  )

  const row = token => (
    <TokenRow
      key={token.key}
      token={token}
      value={tokens[token.key]}
      saved={savedTokens[token.key]}
      onChange={v => setToken(token.key, v)}
      disabled={disabled}
      error={errors[`token:${token.key}`]}
      pair={CONTRAST_PAIRS[token.key]?.(tokens)}
      bindSpot={bindSpot}
    />
  )

  const dialogCopy = {
    create: { title: 'New theme', description: 'Name it, pick a starting point, then fine-tune the colours.', confirm: 'Create theme' },
    saveAs: { title: 'Save as theme', description: 'Keep these colours as a theme you can come back to.', confirm: 'Save theme' },
    rename: { title: 'Rename theme', description: null, confirm: 'Rename' },
  }[shownDialog?.mode || 'create']
  const dialogInitial = shownDialog?.mode === 'rename' ? shownDialog.theme.name : uniqueName('Custom theme', allThemes)

  return (
    <div className="space-y-4">
      <Panel
        title="Themes"
        description="Pick a theme to preview it. Press Save to apply it for everyone."
        actions={!disabled && (
          <>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="btn btn-secondary btn-sm gap-1.5 disabled:opacity-60">
              <Upload className="h-3.5 w-3.5" /> Import
            </button>
            <button type="button" onClick={openCreate} disabled={busy} className="btn btn-primary btn-sm gap-1.5 disabled:opacity-60">
              <Plus className="h-3.5 w-3.5" /> New theme
            </button>
            <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={importFile} />
          </>
        )}
      >
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">Built-in</p>
            <div className={TILE_GRID}>
              {THEME_PRESETS.map(theme => tile(theme, builtInMenu(theme)))}
              {!savedIsTile && tile(savedTheme, disabled ? null : [
                { label: 'Save as theme', icon: Save, onClick: () => { pick(savedTheme); setDialog({ mode: 'saveAs' }) } },
              ])}
            </div>
          </div>

          {logo.status !== 'idle' && (
            <div>
              <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">From your logo</p>
                {logo.colors.length > 0 && (
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                    {logoUrl && (
                      <img src={logoUrl} alt="" className="h-4 w-4 rounded-sm object-contain" />
                    )}
                    Colours found
                    {logo.colors.map(hex => (
                      <span key={hex} className="flex items-center gap-1 rounded-full bg-gray-100 py-0.5 pl-0.5 pr-1.5 font-mono text-[10px] text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                        <span className="h-3 w-3 rounded-full ring-1 ring-inset ring-black/10" style={{ backgroundColor: hex }} />
                        {hex.toUpperCase()}
                      </span>
                    ))}
                  </span>
                )}
              </div>
              {logo.status === 'loading' && (
                <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading logo colours…
                </p>
              )}
              {logo.status === 'error' && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-gray-300 px-4 py-3 dark:border-gray-700">
                  <div className="flex min-w-0 items-center gap-3">
                    {logoUrl && <img src={logoUrl} alt="" className="h-8 w-8 shrink-0 rounded object-contain" />}
                    <div className="min-w-0">
                      <p className="text-[13px] text-gray-700 dark:text-gray-200">Choose your logo file to get matching themes.</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Your saved logo’s storage doesn’t let the browser read its colours. The file is only read here, not uploaded.
                      </p>
                    </div>
                  </div>
                  {!disabled && (
                    <>
                      <button type="button" onClick={() => logoFileRef.current?.click()} className="btn btn-secondary btn-sm shrink-0 gap-1.5">
                        <ImagePlus className="h-3.5 w-3.5" /> Choose logo file
                      </button>
                      <input
                        ref={logoFileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                        className="hidden"
                        aria-label="Logo file to read colours from"
                        onChange={async e => {
                          const file = e.target.files?.[0]
                          e.target.value = ''
                          if (file && !(await logo.readFile(file))) toast.error("Couldn't read that file", 'Try a PNG, JPG, SVG or WebP image.')
                        }}
                      />
                    </>
                  )}
                </div>
              )}
              {logo.status === 'ready' && !suggestions.length && (
                <p className="text-xs text-gray-500 dark:text-gray-400">The logo has no strong colours to build a theme from.</p>
              )}
              {suggestions.length > 0 && (
                <div className={TILE_GRID}>{suggestions.map(theme => tile(theme, suggestionMenu(theme)))}</div>
              )}
            </div>
          )}

          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                Your organisation’s themes
                <span title="Shared with every admin in your organisation" className="inline-flex items-center gap-1 text-gray-400 dark:text-gray-500">
                  <Users className="h-3 w-3" />
                </span>
              </p>
              {library.themes.length > 1 && (
                <button
                  type="button"
                  onClick={() => downloadFile('themes.json', exportThemes(library.themes))}
                  className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                >
                  <Download className="h-3 w-3" /> Export all
                </button>
              )}
            </div>
            {library.themes.length ? (
              <div className={TILE_GRID}>{library.themes.map(theme => tile(theme, customMenu(theme)))}</div>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-300 px-4 py-5 text-center dark:border-gray-700">
                <p className="text-[13px] text-gray-600 dark:text-gray-300">No saved themes yet.</p>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Create one, save the colours below as a theme, or import a theme file.
                </p>
              </div>
            )}
            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
              Saved themes are shared with every admin in your organisation.
            </p>
          </div>
        </div>
      </Panel>

      <Panel
        title="Colours"
        actions={(
          <button
            type="button"
            onClick={onResetTokens}
            disabled={disabled || !customised}
            className={`text-[13px] font-medium text-gray-500 transition-opacity hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100
                        ${customised ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          >
            Reset to default
          </button>
        )}
      >
        <DraftBanner
          editing={editing}
          modified={modified}
          unsaved={!matchesAny && !themeMatches(savedTheme, primaryColor, tokens)}
          onUpdate={() => updateFromDraft(editing)}
          onSaveAs={() => setDialog({ mode: 'saveAs' })}
          onRevert={() => onPickTheme(editing)}
          disabled={disabled}
        />
        <div className="space-y-4">
          {LIVE_GROUPS.map(group => (
            <div key={group.id}>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{group.label}</p>
              <div className="divide-y divide-gray-100 dark:divide-gray-800">{group.tokens.map(row)}</div>
            </div>
          ))}

          <div className="border-t border-gray-100 pt-3 dark:border-gray-800">
            <button
              type="button"
              onClick={() => setShowOther(v => !v)}
              aria-expanded={otherOpen}
              className="flex w-full items-center justify-between rounded-md py-1 text-[13px] font-medium text-gray-600 transition-colors hover:text-gray-900
                         dark:text-gray-400 dark:hover:text-gray-100"
            >
              {OTHER_GROUP.label}
              <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${otherOpen ? 'rotate-180' : ''}`} />
            </button>
            <Collapse open={otherOpen}>
              <p className="pt-1 text-xs text-gray-500 dark:text-gray-400">Not used in the web app yet.</p>
              <div className="divide-y divide-gray-100 dark:divide-gray-800">{OTHER_GROUP.tokens.map(row)}</div>
            </Collapse>
          </div>
        </div>
      </Panel>

      <PromptDialog
        open={Boolean(dialog)}
        title={dialogCopy.title}
        description={dialogCopy.description}
        label="Name"
        initialValue={dialogInitial}
        maxLength={MAX_THEME_NAME}
        confirmLabel={dialogCopy.confirm}
        busy={busy}
        validate={value => nameError(value, allThemes, shownDialog?.theme?.id)}
        onConfirm={confirmDialog}
        onCancel={closeDialog}
      >
        {shownDialog?.mode === 'create' && (
          <div>
            <label htmlFor="settings-theme-start" className="mb-1.5 block text-[13px] font-medium text-gray-700 dark:text-gray-300">Start from</label>
            <select id="settings-theme-start" className="form-input" value={startFrom} disabled={busy} onChange={e => setStartFrom(e.target.value)}>
              <option value="current">Current colours</option>
              {tiles.map(theme => <option key={theme.id} value={theme.id}>{theme.name}</option>)}
            </select>
          </div>
        )}
      </PromptDialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete theme?"
        body={shownDeleting && <>“{shownDeleting.name}” will be removed for everyone in your organisation. The theme in use doesn’t change.</>}
        confirmLabel="Delete"
        busy={busy}
        onConfirm={confirmDelete}
        onCancel={closeDelete}
      />
    </div>
  )
}
